import React, { useMemo } from 'react';
import { Globe, Users, Package, Factory, Link, Waypoints, Sprout, Building2 } from 'lucide-react';
import { useSimulationStore } from '../../store/useSimulationStore';
import KpiCard from '../common/KpiCard';
import StatusBadge, { BadgePreset } from '../common/StatusBadge';
import ItemList, { ItemRowProps } from '../common/ItemList';
import { fmtValue } from '../../utils/format';
import Section from '../common/Section';
import Dot from '../common/StatusDot';

// ── Helper: rank icon ─────────────────────────────────────────────────────────
const rankIcon = (i: number) => (
   <span className="text-gray-600 text-xs font-mono w-5 shrink-0 self-start">{i + 1}</span>
);

// ── DashboardView ─────────────────────────────────────────────────────────────
const DashboardView: React.FC = () => {
   const nodes = useSimulationStore(s => s.nodes);
   const connections = useSimulationStore(s => s.connections);
   const tick = useSimulationStore(s => s.tick);
   const lastUpdate = useSimulationStore(s => s.lastUpdate);
   const setSelectedNodeId = useSimulationStore(s => s.setSelectedNodeId);
   const setSelectedConnectionId = useSimulationStore(s => s.setSelectedConnectionId);
   const resourceNodes = useSimulationStore(s => s.resourceNodes);
   const setSelectedResourceNodeId = useSimulationStore(s => s.setSelectedResourceNodeId);
   
   console.log('DASHBOARD render', { tick, nodes: nodes.length, lastUpdate, 'sample pop': nodes[0]?.population ?? 'N/A' });
   
   const kpi = useMemo(() => {
      const totalPop = nodes.reduce((s, n) => s + (n.population || 0), 0);
      const totalRes = nodes.reduce((s, n) => s + (n.resources || 0), 0);
      const totalIncome = nodes.reduce((s, n) => s + (Number(n.income) || 0), 0);
      const totalEmissions = nodes.reduce((s, n) => s + (n.emissions || 0), 0);
      const totalHousing = nodes.reduce((s, n) => s + (n.housing_units || 0), 0);
      const totalCapacity = nodes.reduce((s, n) => s + (n.capacity_limit || 0), 0);
      const avgStability = nodes.length
         ? nodes.reduce((s, n) => s + (n.stability || 0), 0) / nodes.length : 0;
      
      const richNodes = nodes.filter(n => n.income_status === 'rich').length;
      const middleNodes = nodes.filter(n => n.income_status === 'middle').length;
      const poorNodes = nodes.filter(n => n.income_status === 'poor').length;
      
      const tradingConns = connections.filter(c => (Number(c.last_trade_volume) || 0) > 0);
      const totalTraffic = connections.reduce((s, c) => s + (Number(c.traffic_volume) || 0), 0);
      const totalMaint = connections.reduce((s, c) => s + (Number(c.maintenance_cost) || 0), 0);
      const totalTradeVol = tradingConns.reduce((s, c) => s + (Number(c.last_trade_volume) || 0), 0);
      const totalTradeVal = tradingConns.reduce((s, c) => s + (Number(c.last_trade_value) || 0), 0);
      const totalResourceYield = resourceNodes.reduce((s, rn) => {
         const inRange = nodes.filter(n =>
            Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2) <= rn.radius
         ).length;
         return s + rn.yield_per_tick * inRange;
      }, 0);
      const uncoveredNodes = nodes.filter(n =>
         !resourceNodes.some(rn =>
            Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2) <= rn.radius
         )
      ).length;
      
      const networkHealth =
         avgStability > 0.65 ? 'Healthy' :
            avgStability > 0.40 ? 'Stressed' :
               avgStability > 0.20 ? 'Critical' : 'Collapsing';
      
      const healthColor =
         avgStability > 0.65 ? 'text-green-400' :
            avgStability > 0.40 ? 'text-yellow-400' :
               avgStability > 0.20 ? 'text-orange-400' : 'text-red-400';
      
      return {
         totalPop, totalRes, totalIncome, totalEmissions,
         totalHousing, totalCapacity, avgStability,
         richNodes, middleNodes, poorNodes,
         totalTraffic, totalMaint, tradingConns, totalTradeVol, totalTradeVal,
         totalResourceYield, uncoveredNodes,
         networkHealth, healthColor,
      };
   }, [nodes, connections]);
   
   // Rankings
   const topPop = [...nodes].sort((a, b) => (b.population || 0) - (a.population || 0)).slice(0, 5);
   const topIncome = [...nodes].sort((a, b) => (Number(b.income) || 0) - (Number(a.income) || 0)).slice(0, 5);
   const topEmissions = [...nodes].sort((a, b) => (b.emissions || 0) - (a.emissions || 0)).slice(0, 5);
   const worstStab = [...nodes].sort((a, b) => (a.stability || 0) - (b.stability || 0)).slice(0, 5);
   const mostOvercrowded = [...nodes]
      .filter(n => (n.capacity_limit || 0) > 0)
      .sort((a, b) =>
         (b.population / Math.max(b.capacity_limit, 1)) -
         (a.population / Math.max(a.capacity_limit, 1))
      ).slice(0, 5);
   const topTradeConns = [...connections]
      .filter(c => (Number(c.last_trade_volume) || 0) > 0)
      .sort((a, b) => (Number(b.last_trade_value) || 0) - (Number(a.last_trade_value) || 0))
      .slice(0, 5);
   
   return (
      <div className="space-y-3">
         {/* Top KPIs */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Globe className="w-4 h-4" />} label="Network Health" value={kpi.networkHealth} color={kpi.healthColor} sub={`avg stability ${(kpi.avgStability * 100).toFixed(1)}%`} />
            <KpiCard icon={<Users className="w-4 h-4" />} label="Total Population" value={fmtValue(kpi.totalPop)} color="text-blue-400" sub={`${nodes.length} nodes`} />
            <KpiCard icon={<Package className="w-4 h-4" />} label="Total Resources" value={fmtValue(kpi.totalRes)} color="text-emerald-400" sub={`${fmtValue(kpi.totalIncome)} income/tick`} />
            <KpiCard icon={<Factory className="w-4 h-4" />} label="Total Emissions" value={fmtValue(kpi.totalEmissions)} color="text-red-400" sub={`${nodes.filter(n => n.emissions > 35000).length} near threshold`} />
         </div>
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Link className="w-4 h-4" />} label="Connections" value={connections.length} color="text-white" sub={`${kpi.tradingConns.length} active trades`} />
            <KpiCard icon={<Waypoints className="w-4 h-4" />} label="Total Traffic" value={fmtValue(kpi.totalTraffic)} color="text-blue-300" sub={`${fmtValue(kpi.totalMaint)} maint/tick`} />
            <KpiCard icon={<Dot color="#c084fc" />} label="Trade Volume" value={fmtValue(kpi.totalTradeVol)} color="text-purple-400" sub={`${fmtValue(kpi.totalTradeVal)} credits/tick`} />
            <KpiCard icon={<Sprout className="w-4 h-4" />} label="Resource Yield" value={fmtValue(kpi.totalResourceYield)} color="text-emerald-400" sub={`${kpi.uncoveredNodes} nodes uncovered`} />
         </div>
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Building2 className="w-4 h-4" />} label="Housing Capacity" value={`${fmtValue(kpi.totalHousing)} units`} color="text-cyan-400" sub={`${fmtValue(kpi.totalCapacity)} cap total`} />
         </div>
         
         {/* Status distribution */}
         <Section title="Node Status Distribution">
            <div className="grid grid-cols-3 gap-3 mb-3">
               <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold font-mono text-yellow-300">{kpi.richNodes}</div>
                  <div className="text-xs text-yellow-500 mt-0.5">Rich</div>
               </div>
               <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold font-mono text-blue-300">{kpi.middleNodes}</div>
                  <div className="text-xs text-blue-500 mt-0.5">Middle</div>
               </div>
               <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold font-mono text-gray-300">{kpi.poorNodes}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Poor</div>
               </div>
            </div>
            {nodes.length > 0 && (
               <div className="flex h-2 rounded-full overflow-hidden gap-0.5">
                  <div className="bg-yellow-500 transition-all" style={{ width: `${kpi.richNodes / nodes.length * 100}%` }} />
                  <div className="bg-blue-500 transition-all" style={{ width: `${kpi.middleNodes / nodes.length * 100}%` }} />
                  <div className="bg-gray-500 transition-all" style={{ width: `${kpi.poorNodes / nodes.length * 100}%` }} />
               </div>
            )}
         </Section>
         
         {/* Rankings grid */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            
            <Section title="Top Population">
               <ItemList items={topPop.map((n, i): ItemRowProps => ({
                  id: n.id,
                  icon: rankIcon(i),
                  label: n.name,
                  badge: n.income_status,
                  value: fmtValue(n.population || 0),
                  sub: `${((n.population || 0) / Math.max(n.capacity_limit || 1, 1) * 100).toFixed(0)}% cap`,
                  onClick: () => setSelectedNodeId(n.id),
               }))} />
            </Section>
            
            <Section title="Top Income">
               <ItemList items={topIncome.map((n, i): ItemRowProps => ({
                  id: n.id,
                  icon: rankIcon(i),
                  label: n.name,
                  badge: n.income_status,
                  value: fmtValue(Number(n.income) || 0),
                  sub: `${fmtValue(n.resources || 0)} res`,
                  onClick: () => setSelectedNodeId(n.id),
               }))} />
            </Section>
            
            <Section title="Worst Stability">
               <ItemList items={worstStab.map((n, i): ItemRowProps => ({
                  id: n.id,
                  icon: rankIcon(i),
                  label: n.name,
                  badge: n.income_status,
                  value: `${((n.stability || 0) * 100).toFixed(1)}%`,
                  valueColor: (n.stability || 0) < 0.25 ? 'text-red-300' : 'text-white',
                  onClick: () => setSelectedNodeId(n.id),
               }))} />
            </Section>
            
            <Section title="Most Polluting">
               <ItemList items={topEmissions.map((n, i): ItemRowProps => ({
                  id: n.id,
                  icon: rankIcon(i),
                  label: n.name,
                  badge: n.income_status,
                  value: fmtValue(n.emissions || 0),
                  sub: (n.emissions || 0) > 35000 ? 'near threshold' : undefined,
                  valueColor: (n.emissions || 0) > 40000 ? 'text-red-300' : 'text-white',
                  onClick: () => setSelectedNodeId(n.id),
               }))} />
            </Section>
            
            <Section title="Top Trade Routes">
               {topTradeConns.length === 0
                  ? <p className="text-gray-600 text-xs px-2">No active trade routes yet.</p>
                  : <ItemList items={topTradeConns.map((conn, i): ItemRowProps => {
                     const nA = nodes.find(n => n.id === conn.node_a_id);
                     const nB = nodes.find(n => n.id === conn.node_b_id);
                     return {
                        id: conn.id,
                        icon: rankIcon(i),
                        label: `${nA?.name ?? conn.node_a_id} → ${nB?.name ?? conn.node_b_id}`,
                        value: fmtValue(Number(conn.last_trade_value) || 0),
                        sub: `${fmtValue(Number(conn.last_trade_volume) || 0)} res`,
                        onClick: () => setSelectedConnectionId(conn.id),
                     };
                  })}
                  />
               }
            </Section>
            
            <Section title="Resource Deposits">
               {resourceNodes.length === 0
                  ? <p className="text-gray-600 text-xs px-2">No resource nodes seeded yet.</p>
                  : <ItemList items={[...resourceNodes]
                     .map(rn => {
                        const inRange = nodes.filter(n => Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2) <= rn.radius);
                        return { rn, inRange, totalOut: rn.yield_per_tick * inRange.length };
                     })
                     .sort((a, b) => b.totalOut - a.totalOut)
                     .map(({ rn, inRange, totalOut }, i): ItemRowProps => ({
                        id: rn.id,
                        icon: rankIcon(i),
                        label: rn.name,
                        badge: rn.type,
                        value: fmtValue(totalOut),
                        sub: `${inRange.length} nodes · r${rn.radius}`,
                        onClick: () => setSelectedResourceNodeId(rn.id),
                     }))}
                  />
               }
            </Section>
            
            <Section title="Most Overcrowded">
               <ItemList items={mostOvercrowded.map((n, i): ItemRowProps => {
                  const pct = (n.population || 0) / Math.max(n.capacity_limit || 1, 1);
                  return {
                     id: n.id,
                     icon: rankIcon(i),
                     label: n.name,
                     value: `${(pct * 100).toFixed(0)}%`,
                     sub: `${fmtValue(n.population || 0)} / ${fmtValue(n.capacity_limit || 0)}`,
                     valueColor: pct >= 1.0 ? 'text-red-300' : 'text-white',
                     onClick: () => setSelectedNodeId(n.id),
                  };
               })} />
            </Section>
         </div>
      </div>
   );
};

export default DashboardView;
